import React from 'react'
import { Link } from 'react-router-dom'
import { FreePlanCard, PremiumPlanCard } from '../components/PricingPlans'
import Footer from '../components/Footer'

// A static sample so visitors see the product immediately.
const SAMPLE_STATS = [
  ['Odometer', '84,210 mi'],
  ['Total spent', '$2,184.60'],
]

const SAMPLE_LOGS = [
  {
    date: '9/14/2026',
    meter: '84,210 mi',
    entries: [
      ['Oil & filter change', '$74.40'],
      ['Tire rotation', '$22.00'],
    ],
    total: '$96.40',
  },
  {
    date: '6/2/2026',
    meter: '79,880 mi',
    entries: [['Front brake pads & rotors', '$612.00']],
    total: '$612.00',
  },
  {
    date: '2/11/2026',
    meter: '74,300 mi',
    entries: [
      ['Oil & filter change', '$74.40'],
      ['Engine air filter', '$43.80'],
    ],
    total: '$118.20',
  },
]

function SampleLog() {
  return (
    <div className="bg-surface border-[1.5px] border-ink" aria-hidden="true">
      <div className="px-4 sm:px-5 pt-4 pb-3 flex items-center gap-3 border-b border-line">
        <div className="w-11 h-11 bg-ink text-paper flex items-center justify-center font-extrabold tracking-wide flex-shrink-0">TA</div>
        <div className="min-w-0">
          <p className="font-extrabold text-ink leading-tight">2020 Toyota Tacoma</p>
          <p className="font-mono text-xs text-muted">Sample log</p>
        </div>
      </div>
      <div className="grid grid-cols-2 border-b-[1.5px] border-ink">
        {SAMPLE_STATS.map(([label, value], i) => (
          <div key={label} className={`px-4 sm:px-5 py-3 ${i === 0 ? 'border-r border-line' : ''}`}>
            <p className="text-[11px] font-bold uppercase tracking-widest text-muted">{label}</p>
            <p className="mt-0.5 font-mono text-lg font-medium text-ink">{value}</p>
          </div>
        ))}
      </div>
      <div className="px-4 sm:px-5 py-2.5 flex items-center gap-2 border-b border-accent/40 bg-accent/5">
        <span className="bg-accent text-white text-[10px] font-extrabold tracking-widest px-1.5 py-0.5">OVERDUE</span>
        <span className="text-sm font-bold text-ink">Cabin air filter</span>
      </div>
      <div className="px-4 sm:px-5">
        {SAMPLE_LOGS.map((log) => (
          <div key={log.date} className="py-3.5 border-b border-line last:border-b-0 flex gap-4">
            <div className="w-24 sm:w-28 flex-shrink-0">
              <p className="font-mono text-sm font-semibold text-ink">{log.date}</p>
              <p className="font-mono text-xs text-muted">{log.meter}</p>
            </div>
            <div className="flex-1 min-w-0 space-y-1">
              {log.entries.map(([title, cost]) => (
                <div key={title} className="flex justify-between gap-3 text-sm">
                  <span className="font-semibold text-ink truncate">{title}</span>
                  <span className="font-mono text-muted flex-shrink-0">{cost}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

const STEPS = [
  {
    n: '01',
    title: 'Log each service',
    body: 'Date, odometer or engine hours, shop, cost and notes. Works for cars, trucks, motorcycles, boats and equipment, in miles, kilometers or hours.',
  },
  {
    n: '02',
    title: 'Know what is due',
    body: 'Set the next service by date or mileage. Overdue items show up on your dashboard, and you can be emailed when something comes due.',
  },
  {
    n: '03',
    title: 'Hand over the history',
    body: 'Share a link or export a PDF when you sell. Buyers see dates, services and mileage without needing an account.',
  },
]

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-paper flex flex-col">
      <header className="border-b-[3px] border-ink">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2 min-w-0">
            <img src="/logo-mark.png" alt="" className="h-8 w-8 sm:h-9 sm:w-9 flex-shrink-0" />
            <span className="text-base sm:text-xl font-extrabold tracking-tight text-ink">MyWrenchLog</span>
          </Link>
          <div className="flex items-center gap-4 sm:gap-5 text-sm font-semibold">
            <Link to="/login" className="text-ink hover:underline underline-offset-4">Sign in</Link>
            <Link to="/get-started" className="px-3 sm:px-4 py-2 font-bold text-white bg-accent hover:bg-accent-dark whitespace-nowrap">
              Start free
            </Link>
          </div>
        </div>
      </header>

      <main className="flex-1">
        <section className="max-w-5xl mx-auto px-4 sm:px-6 pt-10 pb-12 sm:pt-16 sm:pb-16 grid lg:grid-cols-[minmax(0,1fr)_minmax(0,440px)] gap-10 lg:gap-14 items-center">
          <div>
            <h1 className="text-4xl sm:text-5xl font-extrabold text-ink leading-[1.05]">
              A simple maintenance log for any vehicle.
            </h1>
            <p className="mt-5 text-lg text-muted max-w-xl">
              Record services, costs and mileage. See what is overdue. Hand over a clean service history when it is time to sell.
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-3">
              <Link to="/get-started" className="px-6 py-3 font-bold text-white bg-accent hover:bg-accent-dark">
                Start your log
              </Link>
              <Link to="/login" className="font-bold text-ink hover:underline underline-offset-4">
                Sign in
              </Link>
            </div>
            <p className="mt-4 text-sm text-muted">Free for one vehicle. No card needed.</p>
          </div>
          <SampleLog />
        </section>

        <section className="border-t border-line">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-ink pb-3 border-b-[3px] border-ink">How it works</h2>
            <div>
              {STEPS.map((step) => (
                <div key={step.n} className="grid sm:grid-cols-[80px_240px_minmax(0,1fr)] gap-x-6 gap-y-1 py-6 border-b border-line">
                  <p className="font-mono text-sm text-accent font-semibold">{step.n}</p>
                  <h3 className="text-lg font-extrabold text-ink">{step.title}</h3>
                  <p className="text-muted">{step.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="border-t border-line">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-ink mb-2">Pricing</h2>
            <p className="text-muted mb-8 max-w-xl">
              One vehicle is free, with unlimited entries. Premium is for more vehicles, receipts and photos on each log, and PDF reports.
            </p>
            <div className="grid md:grid-cols-2 gap-5 sm:gap-6">
              <FreePlanCard variant="landing" linkTo="/get-started" />
              <PremiumPlanCard variant="landing" linkTo="/get-started" />
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  )
}
